"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = require("react");
var _base = require("@site/pattern-library-utils/base.functions");
var _fetchWithHeader = require("../utils/fetchWithHeader");
var _transformConfigData = require("../config/transform-config-data");
var _useSessionStorage = _interopRequireDefault(require("./useSessionStorage"));
var _useRefetch = _interopRequireDefault(require("./useRefetch"));
var _appConfig = require("../config/app-config");
var _apiConfig = _interopRequireDefault(require("../config/api-config"));
var _functions = require("../utils/functions");
/* eslint-disable no-console */

// Custom error class for validation errors
class ValidationError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'ValidationError';
    this.code = code;
  }
}

// Custom error class for HTTP errors that require redirects
class HttpError extends Error {
  constructor(message, code) {
    super(message);
    this.name = 'HttpError';
    this.code = code;
  }
}

/**
 * Handles the response error from the API.
 * @param {Response} response - The response from the API.
 * @throws {HttpError} - The error with the DPP URL for other non-2xx status codes and the basket URL for 404 errors.
 */
const handleResponseError = response => {
  const code = response.status === 404 ? 'BASKET' : 'DPP';
  throw new HttpError(`Request failed with status ${response.status}`, code);
};
const getData = async (apiUrl, abortController) => {
  const visitorCookie = (0, _functions.getVisitorCookie)();
  const response = await (0, _fetchWithHeader.fetchWithHeader)(apiUrl, {
    method: 'GET',
    credentials: 'include',
    headers: {
      Cookie: visitorCookie
    },
    signal: abortController?.signal
  });
  if (!response.ok) {
    handleResponseError(response);
  }
  const json = await response.json();
  return json;
};
const updateData = async (apiUrl, newInstance) => {
  const visitorCookie = (0, _functions.getVisitorCookie)();
  const response = await (0, _fetchWithHeader.fetchWithHeader)(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Cookie': visitorCookie
    },
    body: JSON.stringify(newInstance)
  });
  if (!response.ok) {
    handleResponseError(response);
  }
  const json = await response.json();
  return json;
};
const validateParams = paramsObject => {
  const {
    src,
    plan,
    itemTag
  } = paramsObject;
  if (!src || !Object.values(_appConfig.SOURCE_TYPES).includes(src)) {
    const validSources = Object.values(_appConfig.SOURCE_TYPES).join(', ');
    throw new ValidationError(`Missing or invalid "src" parameter. Must be one of: ${validSources}`, 'INVALID_SRC');
  }

  // For SERP
  if (src === _appConfig.SOURCE_TYPES.SERP) {
    if (!itemTag || itemTag === 'null') {
      throw new ValidationError('Missing "itemTag" parameter', 'MISSING_ITEM_TAG');
    }
  }

  // For FOS and Email Campaigns
  if (src === _appConfig.SOURCE_TYPES.FOS || src === _appConfig.SOURCE_TYPES.EMAIL) {
    if (!plan || plan === 'null') {
      throw new ValidationError('Missing "plan" parameter', 'MISSING_PLAN');
    }
  }
};
const getApiUrl = (baseUrl, queryString) => {
  const apiUrl = new URL(baseUrl);
  apiUrl.search = queryString;
  return apiUrl;
};
const checkResponse = (response, src) => {
  if (src === _appConfig.SOURCE_TYPES.SERP && (!response || Object.keys(response).length === 0 || !response.response)) {
    throw new Error('Missing Domains Data');
  }
};

// Handles common error scenarios in config data operations
const handleConfigError = (err, {
  hostname,
  envConfig,
  setError,
  operation = 'process'
}) => {
  console.warn(`Failed to ${operation} config data:`, err);
  let redirectUrl = null;

  // Handle abort errors by returning early
  if (err.name === 'AbortError') return;

  // Handle HTTP redirect errors
  if (err instanceof HttpError) {
    redirectUrl = err.code === 'BASKET' ? envConfig.getBasketUrl(hostname) : envConfig.getDppUrl(hostname);
  }

  // Handle validation errors for missing itemTag by redirecting to DPP URL
  if (err instanceof ValidationError && err.code === 'MISSING_ITEM_TAG') {
    redirectUrl = envConfig.getDppUrl(hostname);
  }
  if (redirectUrl) {
    window.location.href = redirectUrl;
    return;
  }

  // Handle other errors by logging and setting error state
  setError(err);
};

/**
 * useConfigData hook to fetch config data from SAPI or session storage.
 *
 * @param {string} baseUrl The URL to fetch the config data from if not in session storage or expired.
 * @param {URLSearchParams} appParams The query parameters to append to the baseUrl.
 * @param {*} initialData The initial data to set before fetching.
 * @param {string} hostname The hostname to use for the getBasketUrl.
 * @returns {object} The data, loading, and error state.
 */
function useConfigData(baseUrl, appParams, initialData = null, hostname = 'godaddy.com') {
  const [dataSessionStorage, setDataSessionStorage] = (0, _useSessionStorage.default)(_appConfig.SESSION_STORAGE_KEY, initialData, _appConfig.TTL);
  const [configData, setConfigData] = (0, _react.useState)();
  const [getLoading, setGetLoading] = (0, _react.useState)(true);
  const [postLoading, setPostLoading] = (0, _react.useState)(false);
  const [error, setError] = (0, _react.useState)(null);
  const extractedParams = (0, _react.useMemo)(() => (0, _functions.extractAppParams)(appParams), [appParams]);
  const {
    queryString,
    src
  } = extractedParams;
  const envConfig = (0, _react.useMemo)(() => _apiConfig.default[(0, _base.getDefaultEnv)()], []);
  const apiUrl = (0, _react.useMemo)(() => getApiUrl(baseUrl, queryString), [baseUrl, queryString]);
  const updateState = newData => {
    setConfigData(newData);
    if (src !== _appConfig.SOURCE_TYPES.SERP && window.location.hostname !== 'localhost') {
      setDataSessionStorage(newData);
    }
  };
  const getConfigData = (0, _react.useCallback)(async abortController => {
    setGetLoading(true);
    setError(null);
    try {
      validateParams(extractedParams);
      const response = await getData(apiUrl, abortController);
      checkResponse(response, src);
      const transformedData = (0, _transformConfigData.transformConfigData)(response);
      updateState({
        ...transformedData,
        queryParams: queryString
      });
    } catch (err) {
      handleConfigError(err, {
        hostname,
        envConfig,
        setError,
        operation: 'fetch'
      });
    } finally {
      setGetLoading(false);
    }
  }, [apiUrl, hostname, queryString, src, extractedParams]);
  const postConfigData = (0, _react.useCallback)(async newInstance => {
    setPostLoading(true);
    setError(null);
    try {
      validateParams(extractedParams);
      const response = await updateData(apiUrl, newInstance);
      const transformedData = (0, _transformConfigData.transformConfigData)(response);
      updateState({
        ...transformedData,
        queryParams: queryString
      });
    } catch (err) {
      updateState();
      handleConfigError(err, {
        hostname,
        envConfig,
        setError,
        operation: 'update'
      });
    } finally {
      setPostLoading(false);
    }
  }, [apiUrl, hostname, queryString, src, extractedParams]);
  (0, _react.useEffect)(() => {
    const abortController = new AbortController();
    async function fetchData() {
      const data = dataSessionStorage?.value;
      const isExpired = Date.now() > dataSessionStorage?.expiry;
      const queryParamsChanged = data?.queryParams !== queryString;
      if (!data || isExpired || queryParamsChanged) {
        setDataSessionStorage();
        await getConfigData(abortController);
        return;
      }
      setConfigData(data);
      setGetLoading(false);
    }
    fetchData();
    return () => {
      abortController.abort();
    };
  }, [queryString]);

  // Use the useRefetch hook for periodic refetching
  (0, _useRefetch.default)(getConfigData, _appConfig.TTL_MILLISECONDS);
  return {
    configData,
    postConfigData,
    getLoading,
    postLoading,
    error
  };
}
var _default = exports.default = useConfigData;